import React, { useEffect, useRef, useState } from 'react';
import { BookService } from '../../book/services/bookService';
import { BookCollectionService } from '../services/bookCollectionService';
import { getBookId } from './SortableBookList';

interface Props {
  collectionId: string;
  /** Sách trong bộ, đúng thứ tự hiển thị của bộ */
  books: any[];
}

/** Một mục trong cây của một cuốn sách */
interface SectionNode {
  id: string;
  title: string;
  type?: string;
  order: number;
  /** Cờ của chính sách (màn soạn sách); tắt ở đó là ẩn ở mọi nơi, bộ không bật lại được */
  visible: boolean;
  children: SectionNode[];
}

type BookTree = { status: 'loading' } | { status: 'error' } | { status: 'ok'; roots: SectionNode[] };

const GROUP_TYPES = ['chapter', 'container'];

/** Nhóm (bài, loại học liệu) có mục con hoặc kiểu chapter/container; còn lại là tiết */
const isGroup = (node: SectionNode) => GROUP_TYPES.includes(node.type || '') || node.children.length > 0;
const leavesOf = (node: SectionNode) => node.children.filter((child) => !isGroup(child));
const subGroupsOf = (node: SectionNode) => node.children.filter(isGroup);

/** Dựng cây từ danh sách phẳng mà `book/sections/book/{id}/all` trả về */
const buildTree = (rows: any[]): SectionNode[] => {
  const nodes = new Map<string, SectionNode & { parent?: string | null }>();
  for (const row of rows) {
    const id = String(row.id ?? row._id);
    if (nodes.has(id)) continue;
    nodes.set(id, {
      id,
      title: row.title || row.name || '',
      type: row.type,
      order: Number(row.order ?? 0),
      visible: row.visible_to_readers !== false && row.visible_to_readers !== 'false',
      children: [],
      parent: row.parent_section_id ? String(row.parent_section_id) : null,
    });
  }
  const roots: SectionNode[] = [];
  nodes.forEach((node) => {
    const parent = node.parent ? nodes.get(node.parent) : undefined;
    if (parent) parent.children.push(node);
    else if (!node.parent) roots.push(node);
  });
  const sort = (list: SectionNode[]) => {
    list.sort((a, b) => a.order - b.order);
    list.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
};

const findPath = (nodes: SectionNode[], id: string, trail: SectionNode[] = []): SectionNode[] | null => {
  for (const node of nodes) {
    if (node.id === id) return [...trail, node];
    const found = findPath(node.children, id, [...trail, node]);
    if (found) return found;
  }
  return null;
};

/** Nhóm đầu tiên có thể chọn (chứa tiết) */
const firstSelectable = (nodes: SectionNode[]): SectionNode | null => {
  for (const node of nodes) {
    if (leavesOf(node).length > 0) return node;
    const found = firstSelectable(subGroupsOf(node));
    if (found) return found;
  }
  return null;
};

const countLeaves = (nodes: SectionNode[], isOn: (leaf: SectionNode) => boolean = (l) => l.visible): { on: number; all: number } =>
  nodes.reduce(
    (acc, node) => {
      const nested = countLeaves(subGroupsOf(node), isOn);
      const leaves = leavesOf(node);
      return {
        on: acc.on + nested.on + leaves.filter(isOn).length,
        all: acc.all + nested.all + leaves.length,
      };
    },
    { on: 0, all: 0 }
  );

/** Nhóm không có tiết nào (kể cả trong nhóm con) thì không có gì để bật/tắt, không hiện ở cột trái */
const withLeaves = (nodes: SectionNode[]) => nodes.filter((node) => countLeaves([node]).all > 0);

/** "Lớp 8 - Sáng tạo số cùng AI - Bài 2: …" → "Bài 2: …"; sách không theo bài giữ nguyên tên */
const shortTitle = (title: string) => title.match(/(Bài\s*\d+.*)$/i)?.[1] ?? title;

/**
 * Bật/tắt tiết theo **từng cuốn sách** trong bộ, dạng hai cột: cột trái là
 * các cuốn (mở ra là loại học liệu, sách gộp có thêm cấp bài), cột phải là
 * các tiết của loại học liệu đang chọn, mỗi tiết một công tắc. Gạt công tắc
 * chỉ ẩn tiết **trong bộ này** (`hidden_section_ids` của bộ); sách gốc và các
 * bộ khác có cùng cuốn không đổi.
 */
const CollectionSectionVisibility: React.FC<Props> = ({ collectionId, books }) => {
  const [trees, setTrees] = useState<Record<string, BookTree>>({});
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{ bookId: string; groupId: string } | null>(null);
  const [hidden, setHidden] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Các lần gạt gửi lần lượt: tắt rồi bật nhanh mà hai request chạy song
  // song thì server có thể nhận "bật" trước "tắt" và giữ sai trạng thái.
  const queue = useRef<Promise<void>>(Promise.resolve());

  const bookIds = books.map(getBookId);
  const booksKey = bookIds.join(',');

  const loadBook = async (bookId: string) => {
    setTrees((t) => ({ ...t, [bookId]: { status: 'loading' } }));
    try {
      const res = await BookService.getAllBookSections(bookId);
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setTrees((t) => ({ ...t, [bookId]: { status: 'ok', roots: buildTree(rows) } }));
    } catch (err) {
      console.error('Failed to load book sections:', err);
      setTrees((t) => ({ ...t, [bookId]: { status: 'error' } }));
    }
  };

  const loadHidden = async () => {
    try {
      setHidden(await BookCollectionService.getHiddenSections(collectionId));
    } catch (err) {
      console.error('Failed to load hidden sections:', err);
      setError('Không tải được danh sách tiết đang ẩn của bộ.');
    }
  };

  useEffect(() => {
    loadHidden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  /** Tiết đang hiện trong bộ: sách không tắt và bộ không ẩn */
  const isOn = (bookId: string) => (leaf: SectionNode) => leaf.visible && !(hidden[bookId] ?? []).includes(leaf.id);

  // Tải cây của mọi cuốn (vài cuốn một lượt) để cột trái có số tiết đang hiện
  useEffect(() => {
    let cancelled = false;
    const pending = [...bookIds];
    const worker = async () => {
      while (!cancelled && pending.length > 0) {
        await loadBook(pending.shift()!);
      }
    };
    Promise.all([worker(), worker(), worker(), worker()]);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booksKey]);

  // Mặc định mở cuốn đầu tiên và chọn loại học liệu đầu tiên của nó
  useEffect(() => {
    if (selected || bookIds.length === 0) return;
    const tree = trees[bookIds[0]];
    if (tree?.status !== 'ok') return;
    const first = firstSelectable(tree.roots);
    if (first) {
      setSelected({ bookId: bookIds[0], groupId: first.id });
      setExpandedBooks(new Set([bookIds[0]]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trees, booksKey]);

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setter((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Đổi công tắc ngay rồi mới lưu; lỗi thì tải lại danh sách ẩn thật của bộ
  const setLeafVisible = (bookId: string, leafId: string, visible: boolean) => {
    setHidden((current) => {
      const ids = (current[bookId] ?? []).filter((id) => id !== leafId);
      return { ...current, [bookId]: visible ? ids : [...ids, leafId] };
    });
    setSaving((n) => n + 1);
    queue.current = queue.current.then(async () => {
      try {
        await BookCollectionService.setSectionsVisible(collectionId, bookId, [leafId], visible);
        setError(null);
      } catch (err: any) {
        console.error('Failed to toggle lesson visibility:', err);
        setError(err?.response?.data?.message || 'Không lưu được thay đổi, đã tải lại trạng thái thật của bộ.');
        await loadHidden();
      } finally {
        setSaving((n) => n - 1);
      }
    });
  };

  const badge = (on: number, all: number, active = false) =>
    all === 0 ? (
      <span className="text-xs text-gray-400">—</span>
    ) : (
      <span
        className={`text-xs tabular-nums px-1.5 rounded ${
          active ? 'text-blue-700' : on < all ? 'text-red-600 bg-red-50' : 'text-gray-500'
        }`}
      >
        {on}/{all}
      </span>
    );

  /** Cây nhóm của một cuốn ở cột trái */
  const renderGroups = (bookId: string, nodes: SectionNode[], depth: number): React.ReactNode =>
    withLeaves(nodes).map((node) => {
      const subGroups = withLeaves(subGroupsOf(node));
      const leaves = leavesOf(node);
      const pad = { paddingLeft: `${0.75 + depth * 1}rem` };

      if (subGroups.length > 0) {
        const key = `${bookId}/${node.id}`;
        const open = expandedGroups.has(key) || (selected?.bookId === bookId && !!findPath(subGroups, selected.groupId));
        const { on, all } = countLeaves([node], isOn(bookId));
        return (
          <div key={node.id}>
            <button
              type="button"
              onClick={() => toggleSet(setExpandedGroups, key)}
              className="w-full flex items-center gap-2 py-1.5 pr-3 rounded-md text-left text-sm text-gray-800 hover:bg-gray-50"
              style={pad}
              aria-expanded={open}
            >
              <span className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true">
                ›
              </span>
              <span className="flex-1 min-w-0 truncate" title={node.title}>
                {node.title}
              </span>
              {badge(on, all)}
            </button>
            {open && (
              <>
                {leaves.length > 0 && renderSelectable(bookId, node, depth + 1)}
                {renderGroups(bookId, subGroups, depth + 1)}
              </>
            )}
          </div>
        );
      }
      return renderSelectable(bookId, node, depth);
    });

  const renderSelectable = (bookId: string, node: SectionNode, depth: number) => {
    const active = selected?.bookId === bookId && selected.groupId === node.id;
    const leaves = leavesOf(node);
    return (
      <button
        key={`sel-${node.id}`}
        type="button"
        onClick={() => setSelected({ bookId, groupId: node.id })}
        className={`w-full flex items-center justify-between gap-2 py-1.5 pr-3 rounded-md text-left text-sm transition-colors ${
          active ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
        }`}
        style={{ paddingLeft: `${1.75 + depth * 1}rem` }}
        aria-current={active ? 'true' : undefined}
      >
        <span className={`truncate ${node.visible ? '' : 'line-through text-gray-400'}`} title={node.title}>
          {node.title}
        </span>
        {badge(leaves.filter(isOn(bookId)).length, leaves.length, active)}
      </button>
    );
  };

  const renderSwitch = (on: boolean, onToggle: () => void, label: string, disabled = false) => (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        on ? 'bg-blue-600 border-blue-600' : 'bg-gray-200 border-gray-300'
      }`}
      data-testid="collection-section-toggle"
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  );

  const renderDetail = () => {
    if (!selected) return <p className="text-sm text-gray-500">Chọn một loại học liệu ở cột bên trái.</p>;
    const tree = trees[selected.bookId];
    if (tree?.status !== 'ok') return <p className="text-sm text-gray-500">Đang tải…</p>;
    const path = findPath(tree.roots, selected.groupId);
    if (!path) return <p className="text-sm text-gray-500">Chọn một loại học liệu ở cột bên trái.</p>;
    const group = path[path.length - 1];
    const leaves = leavesOf(group);
    const book = books.find((b) => getBookId(b) === selected.bookId);
    const trail = [shortTitle(book?.title || ''), ...path.slice(0, -1).map((n) => n.title)].filter(Boolean);

    return (
      <div>
        <p className="text-xs text-gray-500 mb-1 truncate" title={book?.title}>
          {trail.join(' › ')}
        </p>
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-900">{group.title}</h3>
          {leaves.length > 0 && (
            <span className="text-xs text-gray-500 shrink-0">
              {leaves.filter(isOn(selected.bookId)).length}/{leaves.length} đang hiện
            </span>
          )}
        </div>
        {!group.visible && (
          <p className="text-sm text-amber-700 mt-1">Cả mục này đang bị tắt trong màn soạn sách, app không nhận các tiết bên dưới.</p>
        )}

        {leaves.length === 0 ? (
          <p className="mt-6 text-sm text-gray-400">Loại học liệu này chưa có mục nào để bật/tắt.</p>
        ) : (
          <ul className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
            {leaves.map((leaf) => {
              const on = isOn(selected.bookId)(leaf);
              // Tắt trong màn soạn sách là ẩn ở mọi nơi; bộ không bật lại được
              const locked = !leaf.visible;
              const toggle = () => !locked && setLeafVisible(selected.bookId, leaf.id, !on);
              return (
                <li key={leaf.id} className="flex items-center gap-4 py-3">
                  {renderSwitch(on, toggle, `${group.title} – ${leaf.title}`, locked)}
                  <button
                    type="button"
                    onClick={toggle}
                    disabled={locked}
                    className={`flex-1 min-w-0 text-left text-sm truncate disabled:cursor-not-allowed ${
                      on ? 'text-gray-900' : 'text-gray-400 line-through'
                    }`}
                    title={leaf.title}
                  >
                    {leaf.title}
                  </button>
                  <span className={`text-xs shrink-0 ${on ? 'text-gray-500' : locked ? 'text-amber-700' : 'text-red-600'}`}>
                    {on ? 'Đang hiện' : locked ? 'Tắt trong màn soạn sách' : 'Đang ẩn'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mt-6" data-testid="collection-section-visibility">
      <div className="mb-4">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Nội dung hiển thị</h2>
          {/* Luôn chiếm chỗ để chữ trạng thái không đẩy bố cục */}
          <span className="text-sm text-gray-500 min-w-[6rem] text-right" aria-live="polite">
            {saving > 0 ? 'Đang lưu…' : ''}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Chọn sách và loại học liệu bên trái rồi gạt công tắc từng tiết. Tiết bị tắt chỉ ẩn với người nhận sách qua
          bộ này (cả giáo viên lẫn học sinh); sách mua lẻ và các bộ khác có cùng cuốn vẫn đủ tiết. Bật lại không phải tải
          lại file.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[18rem_minmax(0,1fr)] border border-gray-200 rounded-lg overflow-hidden">
        <nav
          className="border-b md:border-b-0 md:border-r border-gray-200 p-2 md:max-h-[36rem] md:overflow-y-auto"
          aria-label="Sách trong bộ"
        >
          {books.map((book) => {
            const bookId = getBookId(book);
            const tree = trees[bookId];
            const open = expandedBooks.has(bookId);
            const counts = tree?.status === 'ok' ? countLeaves(tree.roots, isOn(bookId)) : null;
            return (
              <div key={bookId}>
                <button
                  type="button"
                  onClick={() => toggleSet(setExpandedBooks, bookId)}
                  className="w-full flex items-center gap-2 py-2 px-3 rounded-md text-left text-sm text-gray-900 hover:bg-gray-50"
                  aria-expanded={open}
                >
                  <span className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true">
                    ›
                  </span>
                  <span className="flex-1 min-w-0 truncate font-medium" title={book.title}>
                    {shortTitle(book.title || '')}
                  </span>
                  {counts ? badge(counts.on, counts.all) : <span className="text-xs text-gray-400">…</span>}
                </button>
                {open && (
                  <div className="pb-1">
                    {tree?.status === 'ok' ? (
                      withLeaves(tree.roots).length > 0 ? (
                        renderGroups(bookId, tree.roots, 0)
                      ) : (
                        <p className="pl-7 py-1 text-xs text-gray-400">Sách chưa có nội dung.</p>
                      )
                    ) : tree?.status === 'error' ? (
                      <p className="pl-7 py-1 text-xs text-red-600">
                        Không tải được.{' '}
                        <button type="button" className="underline" onClick={() => loadBook(bookId)}>
                          Thử lại
                        </button>
                      </p>
                    ) : (
                      <p className="pl-7 py-1 text-xs text-gray-400">Đang tải…</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-5 min-h-[16rem]">{renderDetail()}</div>
      </div>
    </div>
  );
};

export default CollectionSectionVisibility;
